import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import { Wallet as WalletIcon, ArrowDownCircle, ArrowUpCircle, Loader2, IndianRupee, Sparkles, Zap, Download, Package, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as ChartTooltip, Legend, CartesianGrid } from "recharts";
import DashboardShell from "../components/layout/DashboardShell";
import { Card, Pill, Button } from "../components/common/Primitives";
import api, { formatApiError } from "../lib/api";

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
const QUICK = [500, 1000, 2000, 5000];

function TxnRow({ t }) {
  const isCredit = t.kind === "credit";
  const Icon = isCredit ? ArrowDownCircle : ArrowUpCircle;
  const color = isCredit ? "text-emerald-700" : "text-rose-700";
  return (
    <tr className="border-t border-navy/[0.06]" data-testid={`wallet-txn-row-${t.txn_id}`}>
      <td className="py-2.5 pr-3">
        <div className={`flex items-center gap-2 ${color}`}>
          <Icon size={14} />
          <span className="text-xs font-mono uppercase tracking-wider">{t.kind}</span>
        </div>
      </td>
      <td className="py-2.5 pr-3 text-sm text-ink">{t.reason.replace(/_/g, " ")}</td>
      <td className={`py-2.5 pr-3 text-sm font-mono ${color}`}>
        {isCredit ? "+" : "−"}{inr(t.amount_inr)}
      </td>
      <td className="py-2.5 pr-3 text-xs font-mono text-ink-muted">{inr(t.balance_after_inr)}</td>
      <td className="py-2.5 pr-3 text-[11px] font-mono text-ink-muted">
        {(t.at || "").slice(0, 16).replace("T", " ")}
      </td>
    </tr>
  );
}

export default function Wallet() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(1000);
  const [loading, setLoading] = useState(true);
  const [recharging, setRecharging] = useState(false);
  const [autoForm, setAutoForm] = useState({ enabled: false, threshold_inr: 500, topup_inr: 2000 });
  const [savingAuto, setSavingAuto] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [globalEsignPrice, setGlobalEsignPrice] = useState(25);
  const [buyingBundle, setBuyingBundle] = useState(null);
  const [insights, setInsights] = useState([]);
  const [mandateBusy, setMandateBusy] = useState(false);
  const [downloadMonth, setDownloadMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const load = () => {
    setLoading(true);
    // Race-free load — merge mandate INTO the /wallet/me result so the mandate
    // key can never be dropped by a late-arriving /wallet/me response.
    Promise.all([
      api.get("/wallet/me").catch((e) => { toast.error(formatApiError(e)); return null; }),
      api.get("/wallet/mandate/me").catch(() => ({ data: { mandate: null } })),
      api.get("/wallet/bundles").catch(() => ({ data: { bundles: [], global_price_inr: 25 } })),
      api.get("/wallet/insights?months=6").catch(() => ({ data: { months: [] } })),
    ])
      .then(([me, mandate, bnd, ins]) => {
        if (me?.data) {
          setData({ ...me.data, mandate: mandate?.data?.mandate || null });
          const w = me.data?.wallet || {};
          setAutoForm({
            enabled: !!w.auto_recharge_enabled,
            threshold_inr: w.auto_recharge_threshold_inr ?? 500,
            topup_inr: w.auto_recharge_topup_inr ?? 2000,
          });
        }
        setBundles(bnd?.data?.bundles || []);
        setGlobalEsignPrice(bnd?.data?.global_price_inr || 25);
        setInsights(ins?.data?.months || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const enableMandate = async () => {
    setMandateBusy(true);
    try {
      const { data: r } = await api.post("/wallet/mandate/create", {
        max_amount_inr: Math.min(15000, Math.max(500, Number(autoForm.topup_inr) || 2000)),
        method: "upi",
      });
      if (r.sandbox_local) {
        toast.success("Sandbox mandate activated instantly — auto-recharge will now hit the mandate.");
        load();
        return;
      }
      toast.success("Mandate created. Check your inbox / SMS for the UPI AutoPay approval prompt.");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "mandate_already_active") {
        toast.error(detail.message, {
          duration: 10000,
          action: {
            label: "Refresh",
            onClick: () => load(),
          },
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setMandateBusy(false); }
  };

  const cancelMandate = async () => {
    if (!data?.mandate?.subscription_id) return;
    if (!window.confirm("Revoke your Cashfree wallet mandate? Auto-recharge will fall back to the emailed payment-link flow.")) return;
    setMandateBusy(true);
    try {
      await api.post(`/wallet/mandate/${data.mandate.subscription_id}/cancel`);
      toast.success("Mandate cancelled.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMandateBusy(false); }
  };

  const buyBundle = async (b) => {
    setBuyingBundle(b.bundle_id);
    try {
      const { data: r } = await api.post(`/wallet/bundles/${b.bundle_id}/purchase`);
      if (r.status === "PLACEHOLDER" && r.credited) {
        toast.success(`Sandbox — ${b.name} activated · ₹${b.wallet_credit_inr} credited to your wallet.`);
        load();
        return;
      }
      if (r.payment_session_id) {
        const cashfree = await loadCashfree({ mode: r.cashfree_mode || "sandbox" });
        cashfree.checkout({ paymentSessionId: r.payment_session_id, redirectTarget: "_self" });
        return;
      }
      toast.info("Bundle order created — waiting on Cashfree.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBuyingBundle(null); }
  };

  const recharge = async () => {
    const val = Math.max(100, Math.min(500000, Number(amount) || 0));
    setRecharging(true);
    try {
      const { data: r } = await api.post("/wallet/recharge", { amount_inr: val });
      if (r.status === "PLACEHOLDER" && r.credited) {
        toast.success(`Sandbox mode — ${inr(val)} credited instantly.`);
        load();
        return;
      }
      if (r.payment_session_id) {
        const cashfree = await loadCashfree({ mode: r.cashfree_mode || "sandbox" });
        cashfree.checkout({
          paymentSessionId: r.payment_session_id,
          redirectTarget: "_self",
        });
        return;
      }
      toast.info("Recharge order created — waiting on Cashfree.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRecharging(false);
    }
  };

  const saveAuto = async () => {
    setSavingAuto(true);
    try {
      const body = {
        enabled: autoForm.enabled,
        threshold_inr: Number(autoForm.threshold_inr) || null,
        topup_inr: Number(autoForm.topup_inr) || null,
      };
      await api.patch("/wallet/auto-recharge", body);
      toast.success(autoForm.enabled ? "Auto-recharge enabled." : "Auto-recharge disabled.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingAuto(false); }
  };

  const downloadStatement = async () => {
    try {
      const res = await api.get(`/wallet/statement.csv`, {
        params: { month: downloadMonth },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `venturaz-wallet-${downloadMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const wallet = data?.wallet;
  const txns = data?.txns || [];
  const esignPrice = data?.esign_price;
  const signsRemaining = wallet && esignPrice?.price_inr
    ? Math.floor((wallet.balance_inr || 0) / esignPrice.price_inr)
    : 0;

  return (
    <DashboardShell title="Wallet" subtitle="PAY-AS-YOU-GO · SIGN + SERVICES">
      <Toaster richColors position="top-right" />

      {loading || !data ? (
        <Card><div className="flex items-center gap-2 text-ink-muted text-sm"><Loader2 size={14} className="animate-spin" /> Loading wallet…</div></Card>
      ) : (
        <div className="space-y-6">
          {/* Balance hero */}
          <div className="rounded-lg overflow-hidden bg-gradient-to-br from-navy-deep via-navy to-navy shadow-[0_16px_40px_rgba(15,61,46,0.24)]">
            <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 text-[#FFD700]/80 text-[10px] font-mono uppercase tracking-[0.24em]">
                  <WalletIcon size={12} /> AVAILABLE BALANCE
                </div>
                <div
                  className="mt-3 font-display text-5xl md:text-6xl text-white"
                  data-testid="wallet-balance"
                >
                  {inr(wallet.balance_inr)}
                </div>
                <div className="mt-3 text-white text-sm font-body leading-relaxed">
                  Auto-deducted per e-sign at <span className="text-[#FFD700] font-heading">{inr(esignPrice.price_inr)}/doc</span>
                  {esignPrice.source === "user_override" && (
                    <span className="text-emerald-300"> · your custom rate</span>
                  )}
                  <span className="text-white/80"> · about {signsRemaining} sign{signsRemaining === 1 ? "" : "s"} left</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {esignPrice.source === "user_override" && esignPrice.note && (
                  <Pill tone="gold">{esignPrice.note}</Pill>
                )}
              </div>
            </div>
          </div>

          {/* Recharge */}
          <Card testId="wallet-recharge-card" variant="liquid">
            <div className="overline mb-3 flex items-center gap-2">
              <Sparkles size={12} className="text-gold" /> Top up your wallet
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {QUICK.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt)}
                  data-testid={`wallet-quick-${amt}`}
                  className={
                    "px-4 py-2 rounded-md border text-sm font-heading transition " +
                    (Number(amount) === amt
                      ? "bg-navy text-white border-navy shadow-[0_4px_12px_rgba(15,61,46,0.2)]"
                      : "bg-white text-ink border-navy/15 hover:border-navy/40")
                  }
                >
                  {inr(amt)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col">
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Custom amount (₹)</span>
                <div className="flex items-center gap-2 border border-navy/15 rounded-md px-3 py-2 bg-white focus-within:border-gold">
                  <IndianRupee size={14} className="text-ink-muted" />
                  <input
                    type="number"
                    min={100}
                    max={500000}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="wallet-recharge-input"
                    className="bg-transparent outline-none w-40 font-mono text-sm"
                  />
                </div>
                <span className="text-[10px] text-ink-muted mt-1 font-mono">Min ₹100 · Max ₹5,00,000 per recharge</span>
              </label>
              <Button
                variant="gold"
                onClick={recharge}
                disabled={recharging || !Number(amount)}
                data-testid="wallet-recharge-btn"
              >
                {recharging ? <Loader2 size={14} className="inline animate-spin mr-2" /> : null}
                Recharge {inr(Number(amount) || 0)}
              </Button>
            </div>
          </Card>

          {/* Insights chart */}
          {insights.length > 0 && (
            <Card testId="wallet-insights-card" variant="liquid">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 overline">
                  <TrendingUp size={12} className="text-gold" />
                  <span>Monthly spend by category · last 6 months</span>
                </div>
                {(() => {
                  const totalSpend = insights.reduce((s, m) => s + (m.spend || 0), 0);
                  const totalTop = insights.reduce((s, m) => s + (m.topups || 0), 0);
                  return (
                    <div className="text-[11px] font-mono text-ink-muted">
                      Spent <span className="text-ink font-heading">{inr(totalSpend)}</span> · Loaded <span className="text-ink font-heading">{inr(totalTop)}</span>
                    </div>
                  );
                })()}
              </div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={insights} margin={{ top: 10, right: 12, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#0F3D2E10" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B6B5F", fontFamily: "monospace" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#6B6B5F", fontFamily: "monospace" }}
                            tickFormatter={(v) => v >= 1000 ? `₹${v / 1000}k` : `₹${v}`} />
                    <ChartTooltip
                      contentStyle={{ background: "#0F3D2E", border: "1px solid #FFD700", color: "#fff", fontSize: 12, borderRadius: 6 }}
                      formatter={(v, name) => [`₹${Number(v).toLocaleString("en-IN")}`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                    <Bar dataKey="esign" stackId="spend" fill="#0F3D2E" name="E-Sign" />
                    <Bar dataKey="daas" stackId="spend" fill="#8A6600" name="DaaS" />
                    <Bar dataKey="plans" stackId="spend" fill="#B8860B" name="Plans" />
                    <Bar dataKey="other" stackId="spend" fill="#B0B0A0" name="Other" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Sign Bundles — pre-paid packs */}
          {bundles.length > 0 && (
            <Card testId="wallet-bundles-card" variant="liquid">
              <div className="flex items-center gap-2 overline mb-3">
                <Package size={12} className="text-gold" />
                <span>Sign Bundles · lock in a deeper discount</span>
              </div>
              <div className="text-xs text-ink-muted mb-4 leading-relaxed">
                Pre-pay once, get more wallet credit than you spent. Effective per-sign price sits well below the bulk-discount ladder.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {bundles.map((b) => {
                  const perSign = Math.round((b.price_inr / (b.sign_target_count || 1)) * 100) / 100;
                  const savingsPct = Math.round(((globalEsignPrice - perSign) / Math.max(globalEsignPrice, 1)) * 100);
                  const bonusPct = Math.round(((b.wallet_credit_inr - b.price_inr) / b.price_inr) * 100);
                  return (
                    <div
                      key={b.bundle_id}
                      className={
                        "relative rounded-lg border p-4 flex flex-col transition-all " +
                        (b.featured
                          ? "border-gold shadow-[0_8px_20px_rgba(255,215,0,0.18)] bg-gradient-to-br from-gold/10 to-gold/[0.02]"
                          : "border-navy/10 bg-white hover:border-navy/25")
                      }
                      data-testid={`wallet-bundle-${b.bundle_id}`}
                    >
                      {b.featured && (
                        <div className="absolute -top-2 left-4 px-2 py-0.5 bg-gold text-navy-deep text-[9px] font-mono uppercase tracking-widest rounded">
                          Most popular
                        </div>
                      )}
                      <div className="font-heading text-lg text-ink">{b.name}</div>
                      <div className="text-xs text-ink-muted mt-1 leading-relaxed min-h-[36px]">{b.tagline}</div>
                      <div className="mt-4 flex items-baseline gap-2">
                        <span className="font-display text-3xl text-ink" data-testid={`wallet-bundle-price-${b.bundle_id}`}>{inr(b.price_inr)}</span>
                        {bonusPct > 0 && (
                          <span className="text-[10px] font-mono text-emerald-800 uppercase tracking-wider">+{bonusPct}% bonus</span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-emerald-800 font-mono">
                        Get {inr(b.wallet_credit_inr)} in wallet · ~{b.sign_target_count} signs
                      </div>
                      <div className="mt-1 text-[11px] text-ink-muted font-mono">
                        Effective ₹{perSign}/sign
                        {savingsPct > 0 && <span className="text-emerald-800"> · save {savingsPct}% vs global fee</span>}
                      </div>
                      <div className="flex-1" />
                      <Button
                        variant={b.featured ? "gold" : "primary"}
                        onClick={() => buyBundle(b)}
                        disabled={buyingBundle === b.bundle_id}
                        testId={`wallet-bundle-buy-${b.bundle_id}`}
                        className="mt-4"
                      >
                        {buyingBundle === b.bundle_id ? <Loader2 size={14} className="inline animate-spin mr-2" /> : null}
                        Buy for {inr(b.price_inr)}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Auto-recharge */}
          <Card testId="wallet-autorecharge-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-gold" />
                <span className="overline">Auto-recharge</span>
                {autoForm.enabled && <Pill tone="success">On</Pill>}
                {data?.mandate?.status === "ACTIVE" && (
                  <Pill tone="gold" testId="wallet-mandate-active-pill">Frictionless mandate active</Pill>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={autoForm.enabled}
                  onChange={(e) => setAutoForm((s) => ({ ...s, enabled: e.target.checked }))}
                  data-testid="wallet-autorecharge-toggle"
                />
                Enable auto-recharge
              </label>
            </div>
            <div className="text-xs text-ink-muted mb-4 leading-relaxed">
              {data?.mandate?.status === "ACTIVE" ? (
                <>
                  Your Cashfree mandate is active — top-ups happen automatically the moment your
                  balance dips below the threshold, no email tap needed. Max per debit: <strong>₹{Number(data.mandate.mandate_max_amount || 0).toLocaleString("en-IN")}</strong>.
                </>
              ) : (
                <>
                  When your balance dips below the threshold, we email a one-tap payment link.
                  Want it fully hands-off? <strong>Authorize a Cashfree UPI AutoPay mandate</strong>
                  {" "}and top-ups happen server-side without any interaction.
                </>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <label>
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1 block">Threshold (₹)</span>
                <input
                  type="number"
                  min={50}
                  max={10000}
                  value={autoForm.threshold_inr}
                  onChange={(e) => setAutoForm((s) => ({ ...s, threshold_inr: e.target.value }))}
                  data-testid="wallet-autorecharge-threshold"
                  className="w-full font-mono text-sm border border-navy/15 rounded px-3 py-2 bg-white focus:border-gold outline-none"
                />
                <span className="text-[10px] text-ink-muted font-mono">Min ₹50 · Max ₹10,000</span>
              </label>
              <label>
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1 block">Top-up amount (₹)</span>
                <input
                  type="number"
                  min={500}
                  max={50000}
                  value={autoForm.topup_inr}
                  onChange={(e) => setAutoForm((s) => ({ ...s, topup_inr: e.target.value }))}
                  data-testid="wallet-autorecharge-topup"
                  className="w-full font-mono text-sm border border-navy/15 rounded px-3 py-2 bg-white focus:border-gold outline-none"
                />
                <span className="text-[10px] text-ink-muted font-mono">Min ₹500 · Max ₹50,000</span>
              </label>
              <Button
                variant="gold"
                onClick={saveAuto}
                disabled={savingAuto}
                data-testid="wallet-autorecharge-save"
              >
                {savingAuto ? <Loader2 size={14} className="inline animate-spin mr-2" /> : null}
                Save preferences
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {data?.mandate?.status === "ACTIVE" ? (
                <Button
                  variant="ghost"
                  onClick={cancelMandate}
                  disabled={mandateBusy}
                  testId="wallet-mandate-cancel"
                >
                  {mandateBusy ? <Loader2 size={12} className="inline animate-spin mr-2" /> : null}
                  Revoke mandate
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={enableMandate}
                  disabled={mandateBusy}
                  testId="wallet-mandate-enable"
                >
                  {mandateBusy ? <Loader2 size={12} className="inline animate-spin mr-2" /> : null}
                  Enable frictionless auto-recharge (UPI AutoPay)
                </Button>
              )}
              <span className="text-[11px] text-ink-muted font-mono">
                UPI AutoPay standard cap ₹15,000/debit — Cashfree may require an AFA for higher amounts.
              </span>
            </div>
          </Card>

          {/* Txns */}
          <Card testId="wallet-txns-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div className="overline">Transaction history</div>
              <div className="flex items-center gap-2">
                <input
                  type="month"
                  value={downloadMonth}
                  onChange={(e) => setDownloadMonth(e.target.value)}
                  data-testid="wallet-statement-month"
                  className="border border-navy/15 rounded px-2 py-1.5 text-xs font-mono bg-white focus:border-gold outline-none"
                />
                <Button
                  variant="ghost"
                  onClick={downloadStatement}
                  data-testid="wallet-statement-download"
                >
                  <Download size={12} className="inline mr-1" /> CSV
                </Button>
              </div>
            </div>
            {txns.length === 0 ? (
              <div className="text-ink-muted text-sm italic">No transactions yet — recharge to get started.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                      <th className="text-left py-2 pr-3">Type</th>
                      <th className="text-left py-2 pr-3">Reason</th>
                      <th className="text-left py-2 pr-3">Amount</th>
                      <th className="text-left py-2 pr-3">Balance after</th>
                      <th className="text-left py-2 pr-3">At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t) => <TxnRow key={t.txn_id} t={t} />)}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </DashboardShell>
  );
}
